'use client';

import type React from 'react';
import { useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { FiPlus } from 'react-icons/fi';
import useMeasure from 'react-use-measure';
import { PrismicRichText } from '@prismicio/react';
import type { Content } from '@prismicio/client';
import SplashButton from '@/components/SplashButton';
import { usePathname } from 'next/navigation';

export interface FAQSectionProps {
  data: Content.FaqDocument[];
  labels: Content.LabelsDocument['data'];
  title: string;
  lang: string;
  mainTitle?: string;
  showImplementationButton?: boolean;
  featuredFaq?: Content.FaqDocument | null;
}

// Array of slugs for which to hide the Implementation Approach button
const hideImplementationButtonSlugs = [
  'implementations',
  'integrations',
  'consulting',
  'managed-services',
];

// Array of slugs for which to hide the FAQ Archive button
const hideFaqArchiveButtonSlugs = ['implementations'];

const FAQSection: React.FC<FAQSectionProps> = ({ data, labels, title, lang, mainTitle, showImplementationButton = true, featuredFaq }) => {
  const pathname = usePathname();
  // Check if the current path ends with any of the slugs in the array
  const shouldHideImplementationButton = hideImplementationButtonSlugs.some(
    (slug) => pathname?.endsWith(`/${slug}`) || pathname?.endsWith(`/${slug}/`)
  ) || !showImplementationButton;
  const shouldHideFaqArchiveButton = hideFaqArchiveButtonSlugs.some(
    (slug) => pathname?.endsWith(`/${slug}`) || pathname?.endsWith(`/${slug}/`)
  );
  const visible = featuredFaq ? data.filter((q) => q.id !== featuredFaq.id) : data;
  return (
    <section className="relative overflow-hidden bg-blue-950 px-4 py-16 pb-20 text-slate-50 sm:py-20 sm:pb-28">
      <div className="relative z-10 flex flex-col items-center justify-center">
        <h2 className="text-sm my-4 mb-10 flex items-center gap-3 uppercase bg-gradient-to-b from-yellow-100 to-yellow-600 bg-clip-text font-medium text-transparent tracking-widest">
          <span aria-hidden="true" className="h-px w-6 bg-yellow-500/60" />
          {title || 'FAQs'}
          <span aria-hidden="true" className="h-px w-6 bg-yellow-500/60" />
        </h2>
        <span className="mb-0 text-3xl sm:text-5xl font-light leading-[1.15] text-center">{mainTitle || labels.faq_header || 'FAQs'}</span>
      </div>
      <span className="absolute -top-[350px] left-[50%] z-0 h-[500px] w-[600px] -translate-x-[50%] rounded-full bg-gradient-to-r from-blue-300/20 to-blue-300/20 blur-3xl" />
      <div className="mx-auto mt-12 max-w-3xl">
        <AnimatePresence mode="wait">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 20 }}
            transition={{ duration: 0.5, ease: 'backIn' }}
            className="space-y-4"
          >
            {visible.map((q) => (
              <Question key={q.id} question={q.data.question} answer={q.data.answer} />
            ))}
            {featuredFaq && (
              <Question
                key={featuredFaq.id}
                question={featuredFaq.data.question}
                answer={featuredFaq.data.answer}
              />
            )}
          </motion.div>
        </AnimatePresence>
        {(!shouldHideFaqArchiveButton || !shouldHideImplementationButton) && (
          <div className="mt-20 flex flex-col items-center gap-4 sm:flex-row sm:justify-center">
            {!shouldHideFaqArchiveButton && (
              <SplashButton
                url={`/${lang}/faq`}
                text={labels.faq_button_all || 'FAQ Archive'}
                color="transparent"
                size="md"
                className="min-w-[300px] sm:min-w-[200px]"
              />
            )}
            {!shouldHideImplementationButton && (
              <SplashButton
                url={`/${lang}/implementations`}
                text={labels.faq_button_implementations || 'Our Implementation Approach'}
                color="blue"
                size="md"
                className="min-w-[300px]"
              />
            )}
          </div>
        )}
      </div>
    </section>
  );
};

const Question: React.FC<{
  question: Content.FaqDocument['data']['question'];
  answer: Content.FaqDocument['data']['answer'];
}> = ({ question, answer }) => {
  const [ref, { height }] = useMeasure();
  const [open, setOpen] = useState(false);

  return (
    <motion.div
      animate={open ? 'open' : 'closed'}
      className={`rounded-xl border-[1px] border-blue-950 px-4 transition-colors ${
        open ? 'bg-blue-800' : 'bg-blue-900'
      }`}
    >
      <button
        onClick={() => setOpen((pv) => !pv)}
        data-faq={question}
        className="ga-faq flex w-full items-center justify-between gap-4 py-4"
      >
        <span
          className={`pointer-events-none text-left text-base font-medium transition-colors ${open ? 'text-white' : 'text-white/80'}`}
        >
          {question || 'No question provided'}
        </span>
        <motion.span
          className="pointer-events-none"
          variants={{
            open: {
              rotate: '45deg',
            },
            closed: {
              rotate: '0deg',
            },
          }}
        >
          <FiPlus
            className={`pointer-events-none text-2xl transition-colors ${open ? 'text-white/80' : 'text-white/50'}`}
          />
        </motion.span>
      </button>
      <motion.div
        initial={false}
        animate={{
          height: open ? height : '0px',
          marginBottom: open ? '24px' : '0px',
        }}
        className="faq-answer overflow-hidden text-white/80"
      >
        <div ref={ref}>
          {answer ? <PrismicRichText field={answer} /> : <p>No answer provided</p>}
        </div>
      </motion.div>
    </motion.div>
  );
};

export default FAQSection;
